import { ArtworkImage } from './ArtworkImage';
import React, { useState } from 'react';
import type { FedRaid, UserLanguage } from '../api';
import { districtColor } from './digitalMapGeometry';
import { GameDialog } from './GameDialog';
import '../styles/InnsmouthTracks.css';

/** Circular vector interpretation of the aquatic creature on the uprising token. */
function UprisingToken({ active }: { active: boolean }) {
  return <svg className="innsmouth-uprising-token" data-active={active} viewBox="0 0 48 48" aria-hidden="true" focusable="false">
    <circle className="innsmouth-uprising-rim" cx="24" cy="24" r="22" />
    <circle className="innsmouth-uprising-inner" cx="24" cy="24" r="19" />
    <g className="innsmouth-uprising-creature" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 28 Q11 23 14 17 L18 19 Q16 10 22 8 L25 15 Q30 9 35 13 L31 18
        Q40 18 38 24 L32 25 Q37 30 32 34 L29 30 L28 36 L20 36 L18 31 L13 35 L10 31 Z" />
      <path d="M19 27 Q23 23 28 25 M21 30 L24 33 L27 29 M14 18 L18 23 M30 18 L28 22" />
      <path className="innsmouth-uprising-wave" d="M8 37 Q12 33 16 37 T24 37 T32 37 T40 37 M10 40 Q14 37 18 40 T26 40 T34 40" />
    </g>
    <g className="innsmouth-uprising-eyes"><ellipse cx="21" cy="21" rx="2" ry="1.4" /><ellipse cx="28" cy="21" rx="2" ry="1.4" /></g>
  </svg>;
}

const districts: { key: keyof FedRaid; name: string; ru: string }[] = [
  { key: 'factoryDistrict', name: 'Factory District', ru: 'Фабричный район' },
  { key: 'churchGreen', name: 'Church Green', ru: 'Церковная площадь' },
  { key: 'innsmouthShore', name: 'Innsmouth Shore', ru: 'Побережье Инсмута' },
];

export function InnsmouthTracks({ deepOnesRisingTrack = 0, fedsRaidInnsmouthTrack, language = 'En', style, horizontal = false, onOpen }: {
  deepOnesRisingTrack?: number;
  fedsRaidInnsmouthTrack?: FedRaid;
  language?: UserLanguage;
  style?: React.CSSProperties;
  horizontal?: boolean;
  onOpen: () => void;
}) {
  const [open, setOpen] = useState(false);
  const russian = language === 'Ru';
  const title = russian ? 'Треки Инсмута' : 'Innsmouth tracks';
  const risingTitle = russian ? 'Восстание глубоководных' : 'Deep Ones Rising';
  const fedsTitle = russian ? 'Рейд федералов в Инсмуте' : 'Feds Raid Innsmouth';
  const progress = [`${risingTitle}: ${deepOnesRisingTrack}/6`, ...districts.map(district =>
    `${russian ? district.ru : district.name}: ${fedsRaidInnsmouthTrack?.[district.key] ?? 0}/2`)].join('; ');
  return <>
    <button type="button" className={`innsmouth-tracks${horizontal ? ' innsmouth-tracks-horizontal' : ''}`} style={style} aria-label={`${title}: ${progress}`}
      aria-haspopup="dialog" aria-expanded={open} onClick={event => {
        event.currentTarget.focus({ preventScroll: true });
        onOpen();
        setOpen(true);
      }}>
      <span className="innsmouth-rising-track">
        <span className="innsmouth-track-heading">{risingTitle}</span>
        <span className="innsmouth-track-row" aria-hidden="true">
          {[1, 2, 3, 4, 5, 6].map(space => <UprisingToken key={space} active={space <= deepOnesRisingTrack} />)}
        </span>
      </span>
      <span className="innsmouth-feds-track">
        <span className="innsmouth-track-heading">{fedsTitle}</span>
        <span className="innsmouth-track-row">
          {districts.map(district => {
            const value = fedsRaidInnsmouthTrack?.[district.key] ?? 0;
            const name = russian ? district.ru : district.name;
            return <span key={district.key} className="innsmouth-feds-district"
              style={{ color: districtColor(district.name), borderColor: districtColor(district.name) }}>
              <span className="innsmouth-district-caption" aria-hidden="true">{name}</span>
              <span className="innsmouth-track-row" aria-hidden="true">
                {[1, 2].map(space => <span key={space} className="innsmouth-clue-space" data-active={space <= value}>
                  {space <= value && <ArtworkImage src="/Clue.png" alt="" draggable={false} />}
                </span>)}
              </span>
            </span>;
          })}
        </span>
      </span>
    </button>
    {open && <GameDialog title={title} variant="location" onClose={() => setOpen(false)}>
      <section>
        <h3>{risingTitle}</h3>
        <p>{russian
          ? 'Добавляйте 1 жетон каждый раз, когда открытие врат предотвращено или монстр попадает в вихрь Инсмута. Когда на треке окажется 6 жетонов, Древний немедленно пробуждается.'
          : 'Add 1 token whenever a gate opening is prevented or a monster enters an Innsmouth vortex. When the track reaches 6 tokens, the Ancient One awakens immediately.'}</p>
      </section>
      <section>
        <h3>{fedsTitle}</h3>
        <p>{russian
          ? 'Один раз во время своей фазы передышки сыщик на улице или в локации Инсмута может потратить 1 или 2 улики, чтобы заполнить столько же пустых ячеек трека федералов в своём текущем районе. Когда все шесть ячеек в трёх районах заполнены, немедленно очистите оба трека: рейда федералов в Инсмуте и восстания глубоководных.'
          : 'Once during their Upkeep, an investigator in an Innsmouth street or location may spend 1 or 2 Clues to fill that many empty spaces in their current district’s Feds track. When all six spaces across the three districts are filled, immediately clear both the Feds Raid Innsmouth and Deep Ones Rising tracks.'}</p>
      </section>
    </GameDialog>}
  </>;
}
